'use client';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { forwardRef, useEffect, useRef, useState } from 'react';
import type { PhoneInputProps, CountryData } from 'react-international-phone';
import { PhoneInput, defaultCountries } from 'react-international-phone';
import './phone-input-styles.css';

export const PhoneInputField = forwardRef<
  HTMLInputElement,
  PhoneInputProps & { variant?: 'outlined' | 'default' }
>(function PhoneInputField({ className, variant, ...props }, ref) {
  const [focused, setFocused] = useState(false);
  const phoneInputRef = useRef(ref ?? null);

  const customCountries = defaultCountries.map(country => {
    if (country[0] === 'Slovakia') {
      return ['Slovakia', 'sk', '421', '... ... ...'] as CountryData;
    }
    return country;
  });

  const locale = useLocaleStore(s => s.currentLocale);

  const defaultCountryFromLocale = () => {
    if (locale === 'cs') {
      return 'cz';
    }

    return locale;
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const current = phoneInputRef.current as any;

      if (current && !current.contains(event.target)) {
        setFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [phoneInputRef]);

  return (
    <div
      ref={phoneInputRef as any}
      className={cn(
        'rounded-lg',
        focused ? 'outline outline-1 outline-primary' : 'outline-none',
        variant === 'outlined' ? 'border-1 border border-border' : 'border-none',
        className,
      )}
    >
      <PhoneInput
        defaultCountry={defaultCountryFromLocale()}
        name="phone"
        countries={customCountries}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        inputClassName={cn('bg-input')}
        inputStyle={{
          backgroundColor: 'hsl(var(--input)) !important',
          height: '48px !important',
          paddingLeft: '20px',
          width: '100%',
          borderEndEndRadius: 'var(--radius)',
          borderStartEndRadius: 'var(--radius)',
          border: 'none',
        }}
        countrySelectorStyleProps={{
          buttonClassName: 'bg-input',
          buttonStyle: {
            height: '48px !important',
            backgroundColor: 'hsl(var(--input)) !important',
            borderStartStartRadius: 'var(--radius)',
            borderEndStartRadius: 'var(--radius)',
            paddingLeft: '12px',
            border: 'none',
          },
        }}
        {...props}
      />
    </div>
  );
});

PhoneInputField.displayName = 'PhoneInputField';
